import { Treshold } from "types/graphql";
import Colors from "./Colors";
import { t } from "i18next";
import { useUserPreferencesStore } from "src/lib/store";

export const GET_TRESHOLDS_DEFS = (positiveMode) => {
    return ({
        extremeLow: {
            color: positiveMode ? Colors["blue-2-lisaqua"][600] : Colors.red["800"],
            bgColorClassName: positiveMode ? 'bg-blue-2-lisaqua-800/10' : 'bg-red-500/60', 
            lineWidth: 1
        },
        low: {
            color: positiveMode ? Colors["blue-lisaqua"][800] :  Colors.orange["500"],
            bgColorClassName: positiveMode ? 'bg-blue-lisaqua-600/20' : 'bg-orange-500/40', 
            lineWidth: 0.2
        },
        target: {
            color: Colors.green["800"],
            bgColorClassName: 'bg-green-500/30',
            lineWidth: 0
        },
        high: {
            color: positiveMode ? Colors["blue-lisaqua"][800] : Colors.orange["500"],
            bgColorClassName: positiveMode ? 'bg-blue-lisaqua-600/20' : 'bg-orange-500/40', 
            lineWidth: 0.5
        },
        extremeHigh: {
            color: positiveMode ? Colors["blue-2-lisaqua"][600] : Colors.red["800"],
            bgColorClassName: positiveMode ? 'bg-blue-2-lisaqua-800/10' : 'bg-red-500/60', 
            lineWidth: 1
        },
    })
}

export type Tresholds = { extremeLow?: number, low?: number, target?: number, high?: number, extremeHigh?: number }

const generateTreshold = (extremeLow = null, low = null, target = null, high = null, extremeHigh = null): Tresholds => {
    return {
        extremeLow, low, target, high, extremeHigh
    }
}

export const TRESHOLDS_KEYS = ["extremeLow", "low", "target", "high", "extremeHigh"]
export const NULL_TRESHOLD = generateTreshold();


export function isValueOutOfTresholdsMinMax(tresholds:Partial<Treshold>, value:number) {
    let error;
    if (tresholds?.maximum && (value > tresholds.maximum))
        error = t("status.aboveMaximum", { name: tresholds.unit, value: tresholds.maximum });
    if (tresholds?.minimum && (value < tresholds.minimum))
        error = t("status.belowMinimum", { name: tresholds.unit, value: tresholds.minimum });
    return error;
}

export function getWarningFromTresholds(tresholds, value, positiveMode = false) {
    const TRESHOLDS_DEFS = GET_TRESHOLDS_DEFS(positiveMode);
    if (tresholds && value) {
        //parseFloat to coerce null to NaN
        if (value <= parseFloat(tresholds.extremeLow))
            return { status: "extremeLow", color : TRESHOLDS_DEFS.extremeLow.color, message: t("status.extremeLow", {value:tresholds.extremeLow})}
        else if (value <= parseFloat(tresholds.low))
            return { status: "low", color : TRESHOLDS_DEFS.low.color, message: t("status.low", {value:tresholds.low})}
        else if ((value >= parseFloat(tresholds.extremeHigh)))
            return { status: "extremeHigh", color : TRESHOLDS_DEFS.extremeHigh.color, message: t("status.extremeHigh", {value:tresholds.extremeHigh})}
        else if ((value >= parseFloat(tresholds.high)))
            return { status: "high", color : TRESHOLDS_DEFS.high.color, message: t("status.high", {value:tresholds.high})}
    }
    return null
}
export function getClassnameFromTresholds(tresholds, value, positiveMode = false) {
    const TRESHOLDS_DEFS = GET_TRESHOLDS_DEFS(positiveMode);
    if (tresholds && typeof value == "number") {
        //parseFloat to coerce null to NaN
        if ((value <= parseFloat(tresholds.extremeLow)) || (value >= parseFloat(tresholds.extremeHigh)))
            return TRESHOLDS_DEFS["extremeLow"].bgColorClassName //'bg-red-500/60'
        else if ((value <= parseFloat(tresholds.low)) || (value >= parseFloat(tresholds.high)))
            return TRESHOLDS_DEFS["low"].bgColorClassName //'bg-orange-500/40'
        else if ((value > parseFloat(tresholds.low)) && ((value < parseFloat(tresholds.high)) || !tresholds.high))
            return TRESHOLDS_DEFS["target"].bgColorClassName //'bg-green-500/30'
    }
}

export const TRESHOLDS_AVAILABLE_WITH_MODEL = {
    "adjustmentCoef": "Feeding",
    "alkalinity": "PhysicochemicalAnalysis",
    "aluminium": "PhysicochemicalAnalysis",
    "ammonium": "PhysicochemicalAnalysis",
    "arsenic": "PhysicochemicalAnalysis",
    "bicarbonate": "WaterBalance",
    "black": "MicrobiologicalAnalysis",
    "brokenAntennas": "ObservationalAnalysis",
    "cadmium": "PhysicochemicalAnalysis",
    "calcium": "PhysicochemicalAnalysis",
    "cannibalism": "ObservationalAnalysis",
    "chloride": "PhysicochemicalAnalysis",
    "chromium": "PhysicochemicalAnalysis",
    "coefficientVar": "BiometricAnalysis",
    "concentration": "MicrobiologicalAnalysis",
    "copper": "PhysicochemicalAnalysis",
    "cramped": "ObservationalAnalysis",
    "dbo": "PhysicochemicalAnalysis",
    "dco_soluble": "PhysicochemicalAnalysis",
    "dco": "PhysicochemicalAnalysis",
    "depot": "WaterAnalysis",
    "digestiveTracts": "ObservationalAnalysis",
    "dispersion": "ObservationalAnalysis",
    "faeces": "ObservationalAnalysis",
    "flowRate": "TankControl",
    "foam": "WaterAnalysis",
    "foodQuantity": "Feeding",
    "gillPlugging": "ObservationalAnalysis",
    "greenBig": "MicrobiologicalAnalysis",
    "greenSmall": "MicrobiologicalAnalysis",
    "health": "ObservationalAnalysis",
    "iron": "PhysicochemicalAnalysis",
    "lead": "PhysicochemicalAnalysis",
    "leftovers": "ObservationalAnalysis",
    "magnesium": "PhysicochemicalAnalysis",
    "malformed": "ObservationalAnalysis",
    "manganese": "PhysicochemicalAnalysis",
    "mercury": "PhysicochemicalAnalysis",
    "mes": "WaterAnalysis",
    "moults": "ObservationalAnalysis",
    "mud": "Settling",
    "mvs": "PhysicochemicalAnalysis",
    "necrosis": "ObservationalAnalysis",
    "ngl": "PhysicochemicalAnalysis",
    "nickel": "PhysicochemicalAnalysis",
    "nitrate": "PhysicochemicalAnalysis",
    "nitrite": "PhysicochemicalAnalysis",
    "ntk": "PhysicochemicalAnalysis",
    "oxygen": "WaterAnalysis",
    "pellets": "Seeding",
    "ph": "PhysicochemicalAnalysis",
    "phosphate": "PhysicochemicalAnalysis",
    "phosphorus": "PhysicochemicalAnalysis",
    "potassium": "PhysicochemicalAnalysis",
    "quantity": "BatchInitial",
    "ratioConcentrationOnTotalFlora": "MicrobiologicalAnalysis",
    "ratioAllGreenTotalFlora": "MicrobiologicalAnalysis",
    "recirculation": "WaterAnalysis",
    "rp": "ObservationalAnalysis",
    "salinity": "PhysicochemicalAnalysis",
    "salinityAfter": "WaterTransfer",
    "salinityVar": "BatchInitial",
    "salt": "WaterTransfer",
    "seaweed": "WaterBalance",
    "shrimpCount": "BiometricAnalysis",
    "shrimpsDead": "ObservationalAnalysis",
    "shrimpsDeadCount": "Knackery",
    "shrimpsObserved": "ObservationalAnalysis",
    "smell": "WaterAnalysis",
    "sodium": "PhysicochemicalAnalysis",
    "sugar": "PhysicochemicalAnalysis",
    "temperature": "WaterAnalysis",
    "temperatureVar": "BatchInitial",
    "time": "Settling",
    "totalFlora": "MicrobiologicalAnalysis",
    "transportTime": "BatchInitial",
    "tss": "PhysicochemicalAnalysis",
    "urea": "Seeding",
    "vivacity": "ObservationalAnalysis",
    "volume": "Settling",
    "waterHeight": "TankControl",
    "yellow": "MicrobiologicalAnalysis",
    "zinc": "PhysicochemicalAnalysis",
}
export const TRESHOLDS_AVAILABLE = Object.keys(TRESHOLDS_AVAILABLE_WITH_MODEL)